import {Typography} from '@mui/material';
import {t} from 'i18next';
import {useNavigate} from 'react-router-dom';
import dayjs from 'dayjs';
import {ReactComponent as PlusIcon} from '../../../assests/svg/plus-icon.svg';
import navConstants from '../../../constants/navConstants';
import AurButton from '../../common/aur-button/AurButton';
import AurSvgIcon from '../../common/aur-svg-icon/AurSvgIcon';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  GridFilterItem,
  GridFilterModel,
  GridRowParams,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {
  failedToLoadStatusCodes,
  FILTER_DATE_FORMAT,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../../constants/constants';
import ECASearchTermReportTable from './ECASearchTermTable';
import {getSTR} from '../../../api/STRService';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../../../redux/store';
import {toODataQueryLowercase} from '../../common/aur-data-grid-pro/useServerSideDataGrid';
import {
  setCurrentFieldTemplateId,
  setStrReportId,
} from '../../../redux/slices/strSlice';
import {STR_FLOW} from '../../pages/search-term-report/CreateSTR';
import {
  getParseSTRStatus,
  getSTRStatusString,
  STRStatusType,
} from '../../pages/view-str/hook/useSTRStatus';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
export interface SearchRowType {
  name: string;
  indexName: string;
  createdBy: string;
  status: string;
  searchPopulation: {
    searchId?: string;
    name?: string;
  };
  lastModifiedDate: string;
  id: number;
}

const DEFAULT_SORT_MODEL: GridSortModel = [
  {
    field: 'lastModifiedDate',
    sort: 'desc',
  },
];

const EcaSearchTermReports = () => {
  const stringPath = 'eca.searchTermReport.';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const [haveReqError, setHaveReqError] = useState<boolean>(false);
  const [reqLoading, setReqLoading] = useState<boolean>(false);
  const [searchData, setSearchData] = useState<SearchRowType[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_NO_ROW
  );
  const [totalItems, setTotalItems] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);
  const [gridFilterModel, setGridFilterModel] =
    useState<GridFilterModel | null>(null);
  const [oDataQuery, setODataQuery] = useState<string>('');
  const [filterCount, setFilterCount] = useState<number>(0);

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const {projectId} = useSelector((state: RootState) => state.reports);

  useEffect(() => {
    if (oDataQuery && clientId && projectId) {
      fetchSTR();
    }
  }, [clientId, oDataQuery, projectId]);

  const fetchSTR = async () => {
    setReqLoading(true);
    try {
      const path = `?clientId=${clientId}&projectId=${projectId}&${oDataQuery}`;
      const response = await getSTR(path);
      const dataWithId = await response.data.data.map(
        (item: SearchRowType, index: number) => {
          return {
            ...item,
            id: itemsPerPage * (currentPage - 1) + index + 1,
          };
        }
      );
      setSearchData(dataWithId);
      setTotalItems(response.data.meta.pagination.totalItems);
    } catch (error: unknown) {
      if (
        failedToLoadStatusCodes.includes((error as {status: number}).status)
      ) {
        setHaveReqError(true);
      }
    } finally {
      newFilterModel.current &&
        newFilterModel.current.length > 0 &&
        trackGAEvent({
          eventName: GA_EVENT_NAMES.FILTER,
          click: {
            name: t(`${stringPath}table.${newFilterModel.current[0].field}`),
            type:
              newFilterModel.current[0].operator === 'contains'
                ? GA_CLICK_TYPES.INPUT
                : GA_CLICK_TYPES.DROPDOWN,
            region: GA_REGIONS.BODY,
            action: t('gaTracking.str.filter'),
            ...(newFilterModel.current[0].field === 'status' && {
              details: t('gaTracking.str.filterAction', {
                filterCount: newFilterModel.current[0].value.length,
              }),
            }),
          },
        });
      setReqLoading(false);
    }
  };

  const renderNoReport = () => {
    return (
      <div className="eca-no-searchterm-report">
        <AurSvgIcon src="images/no-str.svg" />

        <div className="eca-no-searchterm-report-content-container">
          <div className="eca-no-searchterm-report-text-container">
            <Typography className=" typo-text headline2_size main-head">
              {t(stringPath + 'noReports')}
            </Typography>
            <Typography className="typo-text body2_size sub-head">
              {t(stringPath + 'createNewSTR')}
            </Typography>
          </div>

          <AurButton
            type="continue"
            size="Medium"
            label={t('eca.searchTermReport.createNewSTRButton')}
            startIcon={<PlusIcon aria-hidden />}
            dataTestId='create-new-str'
            onClick={() => {
              trackGAEvent({
                eventName: GA_EVENT_NAMES.CLICK,
                click: {
                  name: t('gaTracking.str.createNewSTRButton'),
                  type: GA_CLICK_TYPES.BUTTON,
                  region: GA_REGIONS.BODY,
                  action:
                    window.location.host +
                    navConstants.str.baseSTR +
                    `?Flow=${STR_FLOW.CREATE}`,
                },
              });
              navigate(navConstants.str.baseSTR + `?Flow=${STR_FLOW.CREATE}`);
            }}
          />
        </div>
      </div>
    );
  };
  const handleRefreshClick = () => {
    fetchSTR();
  };
  const handleRowClick = (param: GridRowParams) => {
    const {row} = param;
    const {fieldTemplateId, strReportId, name, indexName} = row;
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.str.title'),
        type: GA_CLICK_TYPES.LIST_ITEM,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.str.openedStr'),
      },
    });
    if (getParseSTRStatus(row.status) === STRStatusType.Draft) {
      // Navigate to edit flow of STR
      dispatch(setCurrentFieldTemplateId(row.fieldTemplateId));
      dispatch(setStrReportId(row.strReportId));
      navigate(navConstants.str.baseSTR + `?Flow=${STR_FLOW.DRAFT}`);
    } else {
      // Navigate to view STR Detail
      const queryParam = {
        fieldTemplateId: fieldTemplateId,
        id: strReportId,
        name: name,
        indexName: indexName,
        statusType: getSTRStatusString(row.status),
      };
      navigate(
        navConstants.str.viewSTR +
          `?${new URLSearchParams(queryParam).toString()}`
      );
    }
  };

  const setItemsPerPageHandler = (itemPerPage: number) => {
    setItemsPerPage(itemPerPage);
    setCurrentPage(1);
  };
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  const onSetSortModel = (sortModel: GridSortModel) => {
    const filteredSortModel = sortModel?.filter((model) => model.sort);
    setSortModel(filteredSortModel);
  };
  const [pendingGridFilterModel, setPendingGridFilterModel] =
    useState<GridFilterModel | null>(null);

  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);

  const newFilterModel = useRef<GridFilterItem[] | null>(null);

  const handleOnFilterModelChange = useCallback((model: GridFilterModel) => {
    setPendingGridFilterModel(model);
    setPendingCurrentPage(1);
  }, []);

  const getNewFilterParam = (
    oldFilterModel: GridFilterModel,
    newFilterModel: GridFilterModel
  ) => {
    const oldFilterIds = new Set(oldFilterModel.items.map((item) => item.id));

    return newFilterModel.items.filter((item) => !oldFilterIds.has(item.id));
  };

  useEffect(() => {
    if (!pendingGridFilterModel) return;

    const handler = setTimeout(() => {
      newFilterModel.current = gridFilterModel
        ? getNewFilterParam(gridFilterModel, pendingGridFilterModel)
        : null;
      setGridFilterModel(pendingGridFilterModel);
      setCurrentPage(pendingCurrentPage);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [pendingGridFilterModel]);
  const isFilterApplied = useMemo(() => {
    if (gridFilterModel?.items) {
      return gridFilterModel.items.length > 0;
    }

    return false;
  }, [gridFilterModel]);
  useEffect(() => {
    const updatedGridFilterModel = gridFilterModel
      ? {
          ...gridFilterModel,
          items: gridFilterModel?.items.map((item: GridFilterItem) =>
            item.field === 'lastModifiedDate'
              ? {
                  ...item,
                  value: dayjs(item.value).format(FILTER_DATE_FORMAT),
                  field: 'LastModifiedDate',
                }
              : {
                  ...item,
                  field:
                    item.field.charAt(0).toUpperCase() + item.field.slice(1),
                }
          ),
        }
      : gridFilterModel;
    const ODataQuery = toODataQueryLowercase(
      updatedGridFilterModel ?? {items: []},
      sortModel,
      {
        page: currentPage,
        pageSize: itemsPerPage,
      },
      'CreatedBy'
    );
    setODataQuery(ODataQuery.substring(1));
    setFilterCount(updatedGridFilterModel?.items?.length || 0);
  }, [itemsPerPage, currentPage, sortModel, gridFilterModel]);
  return (
    <>
      <div className="eca-searchterm-report">
        {searchData.length === 0 &&
        !haveReqError &&
        !reqLoading &&
        !isFilterApplied ? (
          renderNoReport()
        ) : (
          <ECASearchTermReportTable
            data={searchData}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            setItemsPerPage={setItemsPerPageHandler}
            isReqLoading={reqLoading}
            haveReqError={haveReqError}
            onErrorRefresh={handleRefreshClick}
            handleRowClick={handleRowClick}
            onSetSortModel={onSetSortModel}
            sortModel={sortModel}
            handleOnFilterModelChange={handleOnFilterModelChange}
            filterCount={filterCount}
          />
        )}
      </div>
      <AurLoadingIndicator overlay={true} isOpen={reqLoading} />
    </>
  );
};

export default EcaSearchTermReports;
